import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The scope a node draws in when its parent's transform and visibility do not reach it
 * (`godot/scene/parentSpace.ts`), such as a plain Node or a CanvasItem under a Node3D. Such a node's three
 * object moves to the nearest world root, outside every ancestor group: its viewport's, or its
 * CanvasLayer's, whose canvas a canvas root draws on. Three hides a whole subtree below one
 * invisible object, and Godot does not.
 */
import { createContext, useCallback, useContext, useState } from 'react';
import { extend } from '@react-three/fiber';
import * as THREE from 'three';
import { spaceFamilyOf } from '../godot/scene/parentSpace.js';
import { isTopLevelItem } from './canvasPaintOrder.js';
import { nodeEscapesParent } from './nodeEscapesParent.js';
import { CanvasSpaceProvider } from './canvasRootScope.js';
const WorldRootContext = createContext(null);
WorldRootContext.displayName = 'WorldRootContext';
/** The object the viewport's scene tree hangs from, or `null` outside a dispatcher. */
function useWorldRoot() {
    return useContext(WorldRootContext);
}
export function WorldRootProvider({ value, children }) {
    return _jsx(WorldRootContext.Provider, { value: value, children: children });
}
/**
 * Mounts a world root and publishes it to `children`. An Object3D, not a Group: three takes
 * `groupOrder` from the nearest Group, so this resets no canvas key. It exists from the first
 * render, so a node that escapes has somewhere to go at once. `NodeDispatcher` and each CanvasLayer
 * mount it before their children, with no transform of its own, so three updates it first.
 */
export function WorldRoot({ children }) {
    const [root] = useState(() => new THREE.Object3D());
    return (_jsxs(_Fragment, { children: [_jsx("primitive", { object: root }), _jsx(WorldRootProvider, { value: root, children: children })] }));
}
const ParentTypeContext = createContext(null);
ParentTypeContext.displayName = 'ParentTypeContext';
/** The type of the node rendering this subtree, which only that node knows. Null outside a dispatcher. */
export function useParentType() {
    return useContext(ParentTypeContext);
}
/** The space family of the node rendering this subtree. */
export function useParentSpaceFamily() {
    const parentType = useParentType();
    return parentType === null ? null : spaceFamilyOf(parentType);
}
export function ParentTypeProvider({ value, children }) {
    return _jsx(ParentTypeContext.Provider, { value: value, children: children });
}
/**
 * Wraps a node's whole render, moving it to the world root when it escapes its parent. A node that
 * nests normally gets no wrapper. Outside a dispatcher there is no world root, and the node stays
 * where it is.
 */
export function ParentSpaceScope({ node, children }) {
    const escapes = nodeEscapesParent(node, useParentSpaceFamily());
    const worldRoot = useWorldRoot();
    if (!escapes || !worldRoot)
        return _jsx(_Fragment, { children: children });
    // No ancestor CanvasItem transform reaches the world root, so the CanvasSpace a canvas root
    // inside inverts is empty.
    return (_jsx(AttachedToObject, { target: worldRoot, children: _jsx(CanvasSpaceProvider, { value: null, children: children }) }));
}
/**
 * Moves only the three object, not the React subtree: an R3F portal would hand every component
 * inside its own `scene`, which WorldEnvironment, Decal and the light helpers write to. R3F calls a
 * function `attach` on every mount and reorder, and its cleanup on removal. An `object3D`, not a
 * group, so no canvas key resets. The key remounts it into a new target.
 */
export function AttachedToObject({ target, children }) {
    // Stable per target: R3F detaches and attaches again when the function changes.
    const attachToTarget = useCallback((_parent, self) => {
        target.add(self);
        return () => target.remove(self);
    }, [target]);
    return (_jsx("object3D", { attach: attachToTarget, children: children }, target.uuid));
}
/**
 * An object whose world matrix composes from `space`, not from its parent, as a `top_level`
 * Node3D's does (`node_3d.cpp:656-660`). It stays in its parent's subtree, where visibility still
 * reaches it (`:1132-1143`). `space` is the world root, which three updates first.
 */
class SpaceAnchoredObject extends THREE.Object3D {
    space;
    constructor(space) {
        super();
        this.space = space;
    }
    updateMatrixWorld(_force) {
        this.composeFromSpace();
        this.matrixWorldNeedsUpdate = false;
        for (const child of this.children)
            child.updateMatrixWorld(true);
    }
    updateWorldMatrix(updateParents, updateChildren) {
        if (updateParents)
            this.space.updateWorldMatrix(true, false);
        this.composeFromSpace();
        if (updateChildren)
            for (const child of this.children)
                child.updateWorldMatrix(false, true);
    }
    composeFromSpace() {
        if (this.matrixAutoUpdate)
            this.updateMatrix();
        this.matrixWorld.multiplyMatrices(this.space.matrixWorld, this.matrix);
    }
}
/** An element, not a `<primitive>`: R3F builds it from `args`, so it takes children like any group. */
const SpaceAnchored = extend(SpaceAnchoredObject);
/** Wraps a `top_level` Node3D's content so its parent's transform stops reaching it. */
export function TopLevelScope({ node, children }) {
    const worldRoot = useWorldRoot();
    const isTopLevel = isTopLevelItem(node) && spaceFamilyOf(node.type) === 'Node3D';
    if (!isTopLevel || !worldRoot)
        return _jsx(_Fragment, { children: children });
    return _jsx(SpaceAnchored, { args: [worldRoot], children: children });
}
//# sourceMappingURL=parentSpaceScope.js.map